import { computed, shallowRef, triggerRef, type Component, type ComputedRef } from 'vue';

import { onScopeDisposeOrWarn } from '@platform/vue/on-scope-dispose-or-warn';

/**
 * A pluggable panel surface contributed to the DevTools HUD. Any layer that can
 * import `@abax/platform` (the shell, dev-shell, abax-ui, an MFE) registers a
 * section once and the panel renders it — `@abax/platform` itself stays
 * provider-agnostic and never imports the contributor.
 */
export interface DevToolsSection {
  /** Stable identity. Re-registering the same id replaces the previous entry. */
  id: string;
  /** Human-readable label shown in the tab strip or card header. */
  label: string;
  /** Optional icon component; rendered via `<component :is>` by the host. */
  icon?: unknown;
  /** The surface to mount. */
  component: Component;
  /** `tab` renders as a top-level tab; `card` renders inside the InspectorCard stack. */
  placement: 'tab' | 'card';
  /** Sort weight (ascending). Ties break alphabetically by label. */
  order?: number;
  /** For `card` placement — whether the accordion starts expanded. */
  defaultOpen?: boolean;
}

interface SectionRegistry {
  /** id -> section (dedup by id, last-wins) */
  sections: Map<string, DevToolsSection>;
  /** Cross-copy notification: each mounted consumer registers a callback here. */
  subscribers: Set<() => void>;
}

// Process-wide singleton anchored to `window`, mirroring `getRegistry` in
// icons/icon-provider.ts. @abax/platform is NOT a Module Federation singleton,
// so the shell and each consumer evaluate their own copy of this module — a
// module-local registry would hide sections one layer registered from another.
// The window anchor gives every copy one registry. Falls back to a module-local
// registry where `window` is absent (SSR, Node test env).
const REGISTRY_KEY = '__ABAX_DEV_TOOLS_SECTIONS__';
let fallbackRegistry: SectionRegistry | undefined;

function createRegistry(): SectionRegistry {
  return { sections: new Map(), subscribers: new Set() };
}

function getRegistry(): SectionRegistry {
  if (typeof window === 'undefined') {
    fallbackRegistry ??= createRegistry();
    return fallbackRegistry;
  }
  const w = window as unknown as Record<string, SectionRegistry | undefined>;
  w[REGISTRY_KEY] ??= createRegistry();
  return w[REGISTRY_KEY] as SectionRegistry;
}

// Reactivity bridge: the registry is a plain Map, so a register call cannot be
// tracked by Vue on its own. A shallowRef bumped on every register lets
// `useDevToolsSections` re-derive its computed so a late-registered section
// makes the mounted panel re-render.
//
// The shallowRef is module-local, so a register call from a different
// @abax/platform copy (e.g. an MFE) would bump only that copy's ref, leaving
// the host computed stale. The `subscribers` Set on the shared window registry
// breaks the isolation: each mounted consumer registers a callback that
// triggers its own local ref, and every register call notifies all of them.
const registryVersion = shallowRef(0);

/**
 * Contribute a section to the DevTools panel. Dedupes by {@link DevToolsSection.id}
 * (last-wins) and notifies any mounted panel to re-render.
 */
export function registerDevToolsSection(section: DevToolsSection): void {
  const registry = getRegistry();
  registry.sections.set(section.id, section);
  registry.subscribers.forEach((cb) => cb());
}

/**
 * Reactive, sorted view of the registered sections. Sorts by `order` ascending
 * (missing `order` sinks to the end) then by `label` alphabetically. Recomputes
 * whenever {@link registerDevToolsSection} is called.
 */
export function useDevToolsSections(): ComputedRef<DevToolsSection[]> {
  const notify = () => {
    registryVersion.value += 1;
    triggerRef(registryVersion);
  };
  const registry = getRegistry();
  registry.subscribers.add(notify);
  onScopeDisposeOrWarn(() => registry.subscribers.delete(notify), 'useDevToolsSections()');

  return computed(() => {
    // Touch the version ref so register calls invalidate this computed.
    void registryVersion.value;
    return [...getRegistry().sections.values()].sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return a.label.localeCompare(b.label);
    });
  });
}

/** @internal Test/HMR helper — clears all registered sections. */
export function __resetDevToolsSections(): void {
  const registry = getRegistry();
  registry.sections.clear();
  registry.subscribers.forEach((cb) => cb());
}
